/**
 * Removes the "Made with Emergent" badge if it's injected at runtime.
 * Belt-and-suspenders defense alongside CSS rules in index.css.
 *
 * Strategy:
 *  1. Sweep the DOM on load and remove any matching nodes.
 *  2. Install a MutationObserver to catch later injections.
 *  3. Re-sweep periodically for a short window (covers async script timing).
 */

const SELECTORS = [
  "#emergent-badge",
  'a[id="emergent-badge"]',
  'a[href*="app.emergent.sh"]',
  'a[href*="emergent.sh/?utm_source=emergent-badge"]',
  '[data-emergent-badge]',
];

const matchesBadge = (el) => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.id === "emergent-badge") return true;
  if (el.tagName === "A") {
    const href = el.getAttribute("href") || "";
    if (href.includes("emergent.sh")) return true;
  }
  // Catch the script tag too
  if (el.tagName === "SCRIPT") {
    const src = el.getAttribute("src") || "";
    if (src.includes("assets.emergent.sh/scripts/emergent-main")) return true;
  }
  return false;
};

const sweep = () => {
  try {
    SELECTORS.forEach((sel) => {
      document.querySelectorAll(sel).forEach((node) => node.remove());
    });
    // Also catch any script that re-injects
    document
      .querySelectorAll('script[src*="assets.emergent.sh/scripts/emergent-main"]')
      .forEach((s) => s.remove());
  } catch (_) {
    /* noop */
  }
};

export const installBadgeRemover = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  // Initial sweep
  sweep();
  // After fonts/scripts settle
  window.setTimeout(sweep, 150);
  window.setTimeout(sweep, 600);
  window.setTimeout(sweep, 1500);
  window.setTimeout(sweep, 3000);

  // Watch for any later injection
  try {
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (matchesBadge(node)) {
            node.remove();
            return;
          }
          if (node instanceof HTMLElement) {
            // Check children too
            node.querySelectorAll && SELECTORS.forEach((sel) => {
              node.querySelectorAll(sel).forEach((c) => c.remove());
            });
          }
        });
      }
    });
    obs.observe(document.documentElement, { childList: true, subtree: true });
  } catch (_) {
    /* noop */
  }
};

export default installBadgeRemover;
